import { useEffect, useMemo, useState } from 'react';
import 'jsoneditor-react/es/editor.min.css';
import ace from 'brace';
import 'brace/mode/json';
import 'brace/theme/monokai';
import 'brace/ext/searchbox';

import Stack from 'rsuite/Stack';
import Button from 'rsuite/Button';
import Message from 'rsuite/Message';
import Loader from 'rsuite/Loader';
import { useToaster } from 'rsuite/esm/toaster';

// @ts-ignore - package typings are often incomplete
import { JsonEditor } from 'jsoneditor-react';

import './PrepListSettings.css';
import { useProducts } from './hooks/useProducts';
import { useUpdateProducts } from './hooks/useUpdateProducts';

type Product = {
  id?: number | string;
  [key: string]: unknown;
};

type ProductsApiResponse = {
  products: Product[];
};

type ProductEditorItem = Omit<Product, 'id'>;

type ProductsEditorValue = {
  products: ProductEditorItem[];
};

type AppError = {
  message?: string;
};

type ValidationResult = {
  valid: boolean;
  errors: string[];
};

type JsonEditorValidationError = {
  path?: Array<string | number>;
  message?: string;
};

type JsonEditorCodeProps = {
  value: unknown;
  mode?: 'code' | 'tree';
  ace?: typeof ace;
  theme?: string;
  navigationBar?: boolean;
  statusBar?: boolean;
  allowedModes?: Array<'code' | 'tree'>;
  onChangeText?: (jsonText: string) => void;
  onValidationError?: (errors: JsonEditorValidationError[]) => void;
};

const TypedJsonEditor = JsonEditor as unknown as React.ComponentType<JsonEditorCodeProps>;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const validateProductsJson = (value: unknown): ValidationResult => {
  const errors: string[] = [];

  if (!isRecord(value)) {
    return {
      valid: false,
      errors: ['Root value must be a JSON object.']
    };
  }

  if (!('products' in value)) {
    errors.push("Missing required property 'products'.");
    return {
      valid: false,
      errors
    };
  }

  const { products } = value;

  if (!Array.isArray(products)) {
    errors.push("'products' must be an array.");
    return {
      valid: false,
      errors
    };
  }

  products.forEach((item, index) => {
    if (!isRecord(item)) {
      errors.push(`products[${index}] must be an object.`);
      return;
    }

    // Optional example field checks.
    // Adjust these to match your real API rules.
    if ('name' in item && typeof item.name !== 'string') {
      errors.push(`products[${index}].name must be a string.`);
    }

    if ('price' in item && typeof item.price !== 'number') {
      errors.push(`products[${index}].price must be a number.`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

const formatJson = (value: unknown): string => JSON.stringify(value, null, 2);

const tryParseJson = (
  text: string
): { success: true; value: unknown } | { success: false; error: string } => {
  try {
    return {
      success: true,
      value: JSON.parse(text)
    };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Invalid JSON syntax.';
    return {
      success: false,
      error: message
    };
  }
};

const Products = (): JSX.Element => {
  /**
   * editorSeed:
   * Initial value loaded into JsonEditor. We don't continuously push every keystroke
   * back through the `value` prop, because that can interfere with the code editor UX.
   */
  const [editorSeed, setEditorSeed] = useState<ProductsEditorValue | null>(null);

  /**
   * rawJsonText:
   * Exact text currently in the editor.
   */
  const [rawJsonText, setRawJsonText] = useState<string>('');

  /**
   * parsedJson:
   * Current parsed JSON from rawJsonText, only when syntax is valid.
   * This is what we save.
   */
  const [parsedJson, setParsedJson] = useState<ProductsEditorValue | null>(null);

  /**
   * syntaxError:
   * JSON parse error from invalid text in code mode.
   */
  const [syntaxError, setSyntaxError] = useState<string | null>(null);

  /**
   * schemaErrors:
   * Shape/business validation errors after JSON parses successfully.
   */
  const [schemaErrors, setSchemaErrors] = useState<string[]>([]);

  const {
    data,
    isLoading,
    error,
    refetch
  } = useProducts() as {
    data?: ProductsApiResponse;
    isLoading: boolean;
    error?: unknown;
    refetch: () => void;
  };

  const {
    mutate: updateProducts,
    isLoading: savingInProgress
  } = useUpdateProducts() as {
    mutate: (
      payload: ProductsEditorValue,
      options?: {
        onSuccess?: () => void;
        onError?: () => void;
      }
    ) => void;
    isLoading: boolean;
  };

  const toaster = useToaster();

  useEffect(() => {
    if (!data?.products || !Array.isArray(data.products)) {
      return;
    }

    const productsWithoutId: ProductsEditorValue = {
      products: data.products.map(({ id, ...productWithoutId }) => productWithoutId)
    };

    setEditorSeed(productsWithoutId);
    setParsedJson(productsWithoutId);
    setRawJsonText(formatJson(productsWithoutId));
    setSyntaxError(null);

    const result = validateProductsJson(productsWithoutId);
    setSchemaErrors(result.errors);
  }, [data]);

  const handleChangeText = (jsonText: string): void => {
    setRawJsonText(jsonText);

    const parsed = tryParseJson(jsonText);

    if (!parsed.success) {
      setSyntaxError(parsed.error);
      setParsedJson(null);
      setSchemaErrors([]);
      return;
    }

    setSyntaxError(null);

    const validation = validateProductsJson(parsed.value);
    setSchemaErrors(validation.errors);

    if (validation.valid) {
      setParsedJson(parsed.value as ProductsEditorValue);
    } else {
      setParsedJson(null);
    }
  };

  /**
   * Optional:
   * If the editor supplies validation errors, you can use them as extra context.
   * We still rely primarily on try/catch parsing because it is predictable and fully under our control.
   */
  const handleEditorValidationError = (errors: JsonEditorValidationError[]): void => {
    if (!errors || errors.length === 0) {
      return;
    }

    // If you want, you could merge these into a separate UI list.
    // For now we let tryParseJson drive syntax messaging.
  };

  const canSave = useMemo(() => {
    return !savingInProgress && !syntaxError && schemaErrors.length === 0 && parsedJson !== null;
  }, [savingInProgress, syntaxError, schemaErrors, parsedJson]);

  const handleSave = (): void => {
    if (syntaxError) {
      toaster.push(
        <Message showIcon type="error" closable>
          Cannot save because the JSON syntax is invalid.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    if (!parsedJson) {
      toaster.push(
        <Message showIcon type="warning" closable>
          No valid JSON is available to save.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    const validation = validateProductsJson(parsedJson);

    if (!validation.valid) {
      setSchemaErrors(validation.errors);

      toaster.push(
        <Message showIcon type="error" closable>
          Cannot save. Please fix the validation errors first.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    updateProducts(parsedJson, {
      onSuccess: () => {
        toaster.push(
          <Message showIcon type="success" closable>
            Products saved successfully.
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );

        refetch();
      },
      onError: () => {
        toaster.push(
          <Message showIcon type="error" closable>
            Error while saving products. Please try again.
          </Message>,
          { placement: 'topEnd', duration: 3000 }
        );
      }
    });
  };

  if (isLoading) {
    return <Loader center />;
  }

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? (error as AppError).message
        : 'Unknown error';

    return (
      <div className="alert alert-danger" role="alert">
        Error loading products: {errorMessage}
      </div>
    );
  }

  if (!editorSeed) {
    return <Loader center />;
  }

  return (
    <div>
      <TypedJsonEditor
        value={editorSeed}
        onChangeText={handleChangeText}
        onValidationError={handleEditorValidationError}
        mode="code"
        ace={ace}
        theme="ace/theme/monokai"
        navigationBar={true}
        statusBar={true}
        allowedModes={['code', 'tree']}
      />

      {syntaxError && (
        <div className="alert alert-danger" style={{ marginTop: '12px' }}>
          <strong>JSON syntax error:</strong>
          <div>{syntaxError}</div>
        </div>
      )}

      {!syntaxError && schemaErrors.length > 0 && (
        <div className="alert alert-danger" style={{ marginTop: '12px' }}>
          <strong>Validation errors:</strong>
          <ul style={{ marginBottom: 0 }}>
            {schemaErrors.map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <Stack style={{ marginTop: '8px', marginBottom: '8px' }} direction="row-reverse">
        <Button onClick={handleSave} appearance="primary" disabled={!canSave}>
          {savingInProgress && <Loader size="sm" style={{ marginRight: '8px' }} />}
          Save
        </Button>
      </Stack>
    </div>
  );
};

export default Products;